import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { HttpService, HttpRequestModel } from 'src/app/service/httpservice/http.service';
import { JobPointeSearchParams } from 'src/app/shared/models/jobPointeSearchParams';
import { Observable, BehaviorSubject, observable, Subject } from 'rxjs';
import { buildQueryString } from 'src/app/utils/functions';

interface SelectedDataModel {
  id: number;
  jobNumber: string;
  name: string;
  potentialJobDetail?: Object;
  selected?: boolean;
}

interface FacilityAttributesResponse {
  data: {
    id: number
    jobBoardAttributes: {
      id: number
      createdAt: string
      expirationDate: string | null
      facilityId: FacilityAttributesResponse['data']['id']
      jobBoardAttribute: {
        id: FacilityAttributesResponse['data']['jobBoardAttributes'][number]['jobBoardAttributeId']
        name: string
      }
      jobBoardAttributeId: number
      status: false | true
      updatedAt: string
      userId: number | null
    }[]
  }
}

@Injectable({
  providedIn: 'root',
})
export class JobPointeService {
  HttpRequestModel: HttpRequestModel = new HttpRequestModel();

  public filtersClearedSource = new BehaviorSubject<{ filterType?: string }>({});
  public filtersCleared = this.filtersClearedSource.asObservable();

  private totalCountSource = new BehaviorSubject(0);
  currentTotalCount = this.totalCountSource.asObservable();

  private totalCountLabelSource = new BehaviorSubject('');
  currentTotalCountLabel = this.totalCountLabelSource.asObservable();

  private countShouldBeVisibleSource = new BehaviorSubject(true);
  countShouldBeVisible = this.countShouldBeVisibleSource.asObservable();

  private selectedItemsSource = new BehaviorSubject<Array<SelectedDataModel>>([]);
  selectedItems = this.selectedItemsSource.asObservable();

  constructor(private httpService: HttpService) { }

  private defaultEmployeeParams: JobPointeSearchParams = {
    page: 1,
    limit: 25,
    embed:
      'employeeDepartment,employeeUnionLocal,employeeUnionClass,jobBoardJobs,user.thumbnail,employeeTitle,employeeGroup,employeeHomeLocal,employeeNoteLogs',
    order: 'employeeAlias',
    'scope[hasEmployeeId]': 'true',
  };
  private defaultJobBoardEmployeeParams: JobPointeSearchParams = {
    page: 1,
    limit: 1000,
    embed:
      'employeeDepartment,employeeUnionLocal,employeeUnionClass,user.thumbnail,jobBoardJobs,employeeTitle,employeeHomeLocal,employeeNoteLogs',
    order: 'employeeAlias',
    'scope[isOnJobBoard]': 'true',
  };
  private defaultJobParams: JobPointeSearchParams = {
    page: 1,
    limit: 25,
    order: 'jobNumber,DESC',
    embed: 'potentialJobDetail,jobScheduleEmployees',
  };

  private defaultEmployeeTypes: JobPointeSearchParams = {
    order: 'name,ASC',
  };
  private defaultJobBoardCardParams: JobPointeSearchParams = {
    embed: 'facilityJobCardDetails,facilityScheduleEmployees,owner,facilityState,laborPrevailingWage',
    order: 'ownerName,ASC',
  };
  private defaultForemanParams: JobPointeSearchParams = {
    page: 1,
    limit: 1000,
    order: 'employeeAlias,ASC',
    'scope[status]': 1,
    'scope[activeEmployees]': 'true',
    'scope[fieldWorker]': 'true',
  };
  private defaultEmployeeExportParams: JobPointeSearchParams = {
    embed:
      'employeeDepartment,employeeUnionLocal,employeeGroup,employeeTitle,jobBoardJobs,user.thumbnail,employeeUnionClass,employeeHomeLocal',
    order: 'employeeAlias,ASC',
    'scope[hasEmployeeId]': 'true',
  };

  private defaultEmployeeDetailsExportParams: JobPointeSearchParams = {
    embed: 'employeeDepartment,jobBoardJobs,employeeTitle,employeeNoteLogs,employeeJobBoardAttributes'
  }

  private jobBoardExportParams = {
    embed:
      'facilityJobCardDetails,facilityScheduleEmployees,owner,facilityState,facilityJobBoardAttributes,jobs,potentialJobDetail,jobBoardTags',
    order: 'ownerName,ASC',
    exportType: 'default',
  };

  private jobBoardFacilityCardExportParams = {
    embed:
      'facilityCardExportJobDetail,owner,facilityState,jobBoardTags,jobCardExportJobBoardAttributes,jobCardExportScheduleEmployees',
    order: 'ownerName,ASC',
    exportType: 'default',
  }

  private jobBoardJobCardExportParams = {
    ...this.jobBoardFacilityCardExportParams,
    embed: 'jobCardExportJobDetail,owner,facilityState,jobCardExportJobBoardAttributes,jobBoardTags,jobCardExportScheduleEmployees'
  }

  setTotalCount(value: number) {
    this.totalCountSource.next(value);
  }

  setTotalCountLabel(value: string) {
    this.totalCountLabelSource.next(value);
  }

  setCountShouldBeVisible(value: boolean) {
    this.countShouldBeVisibleSource.next(value);
  }

  // READ endpoints
  read = {
    employees: (params): Observable<any> => {
      const newParams = { ...this.defaultEmployeeParams, ...params };

      Object.keys(newParams).forEach(key => {
        if (newParams[key] === 'false' && key !== 'scope[status]') {
          delete newParams[key];
        }
      });

      return this.postRequest('GET', `employees/v2`, newParams);
    },

    employee: (id, params): Observable<any> => {
      const newParams = { ...this.defaultEmployeeParams, ...params };

      Object.keys(newParams).forEach(key => {
        if (newParams[key] === 'false' || !newParams[key]) {
          delete newParams[key];
        }
      });

      return this.postRequest('GET', `employees/v2/${id}`, newParams);
    },
    facilities: (params): Observable<any> => this.postRequest('GET', `owner_and_facilities/v2`, { ...params }),
    facilityAttributes: (params): Observable<FacilityAttributesResponse> => this.postRequest('GET', `jobpointe/facilities/attributes/${params.facilityId}`, { ...params }),
    jobBoardEmployees: (params): Observable<any> =>
      this.postRequest('GET', `employees/v2`, {
        ...this.defaultJobBoardEmployeeParams,
        ...params,
      }),
    manageForeman: (params): Observable<any> =>
      this.postRequest('GET', `employees/v2`, {
        ...this.defaultForemanParams,
        ...params,
      }),
    manageJobs: (params): Observable<any> =>
      this.postRequest('GET', `jobs/v2`, {
        ...this.defaultJobParams,
        ...params,
      }),
    jobHistory: (params): Observable<any> =>
      this.postRequest('GET', `jobpointe/job_board_schedules/employeeHistory/${params.userId}`, { filter: 'isHiddenFromEmployeeDetails=0', ...params }),
    job_board_attributes: (params): Observable<any> =>
      this.postRequest('GET', `jobpointe/job_board_attributes`, { ...params }),
    departmentJobBoardCards: (params): Observable<any> =>
      this.postRequest('GET', `jobpointe/job_board_schedules/jobCardData`, {
        ...this.defaultJobBoardCardParams,
        ...params,
      }),
    jobBoardSchedule: (params): Observable<any> =>
      this.postRequest('GET', `jobpointe/job_board_schedules`, { ...params }),
    jobBoardTags: (params): Observable<any> => this.postRequest('GET', `jobpointe/job_board_tags`, { ...params }),
    jobBoardTagAssignments: (params): Observable<any> =>
      this.postRequest('GET', `jobpointe/job_employee_job_board_tags`, {
        ...params,
      }),
    jobBoardTagShapes: (params): Observable<any> =>
      this.postRequest('GET', `jobpointe/job_board_tags/shapes`, { ...params }),
    employeeTypes: (params): Observable<any> =>
      this.postRequest('GET', `employee_titles`, {
        ...this.defaultEmployeeTypes,
        ...params,
      }),
  };

  // COUNT endpoints
  count = {};

  export = {
    employees: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', `employees/export`, {
        ...this.defaultEmployeeExportParams,
        ...params,
      }),
    employee_details: (params: JobPointeSearchParams = {}): Observable<unknown> =>
      this.downloadRequest('POST', `employees/export/${params.employeeId}`, {
        ...this.defaultEmployeeDetailsExportParams,
        ...params
      }),
    jobBoard: (params: JobPointeSearchParams = {}): Observable<any> =>
      this.downloadRequest('POST', 'jobpointe/job_board_schedules/jobCardDataExport', {
        ...this.jobBoardExportParams,
        ...params,
      }),
    jobBoardCard: (params: JobPointeSearchParams = {}, isSingleJob): Observable<any> =>
      this.downloadRequest('POST', 'jobpointe/job_board_schedules/jobCardDataExport', {
        ...(isSingleJob ? this.jobBoardJobCardExportParams : this.jobBoardFacilityCardExportParams),
        ...params,
      })
  };

  // CREATE endpoints
  create = {
    employee_job_board_attributes: (params): Observable<any> =>
      this.postRequest('POST', `jobpointe/employee_job_board_attributes/add`, params),
    employeeCardSchedule: (params): Observable<any> =>
      this.postRequest('POST', `jobpointe/job_board_schedules/addEmployeeToCard`, params),
    jobBoardTag: (params): Observable<any> => this.postRequest('POST', `jobpointe/job_board_tags`, params),
    jobBoardTagAssignment: (params): Observable<any> =>
      this.postRequest('POST', `jobpointe/job_employee_job_board_tags`, params),
    employee_job_board_attributes_batch: (params): Observable<any> =>
      this.postRequest('POST', `jobpointe/employee_job_board_attributes/add_batch`, params),
  };

  // UPDATE endpoints
  update = {
    employees: (payload: any = {}): Observable<any> => this.postRequest('PUT', 'employees/edit', payload),
    employeeCardSchedule: (params): Observable<any> =>
      this.postRequest('POST', `jobpointe/job_board_schedules/moveEmployeeToCard`, params),
    jobHistory: (params: { historyId: number; userId: number }): Observable<any> =>
      this.postRequest('POST', `jobpointe/job_board_schedules/employeeHistory/${params.userId}/${params.historyId}`, { ...params }),
    jobs: (payload: any = {}): Observable<any> => this.postRequest('PUT', 'jobs/update', payload),
    facilities: (payload: any = {}): Observable<any> =>
      this.postRequest('PUT', `owner_and_facilities/v2/update`, payload),
    potentialJobDetails: (payload: any = {}): Observable<any> =>
      this.postRequest('PUT', `jobs/updatePotentialJobDetails/${payload.jobNumber}`, payload),
    jobBoardLock: (payload: any = {}): Observable<any> =>
      this.postRequest('PUT', 'jobpointe/job_board_schedules/toggleCurrentScheduleLock', payload),
    employeeNoteLogArchiveStatus: (id: number, payload: { isArchived: number }): Observable<any> => {
      return this.postRequest('POST', `employee_note_logs/edit/${id}`, payload);
    },
  };

  // ENABLE/DISABLE asset_class
  status = {};

  refine = {};

  // DELETE endpoints
  delete = {
    employeeSchedule: (params: any = undefined): any =>
      this.postRequest('DELETE', 'jobpointe/job_board_schedules/removeEmployeeSchedule', params),
    jobHistory: (params: { historyId: number; userId: number }): Observable<any> =>
      this.postRequest('DELETE', `jobpointe/job_board_schedules/employeeHistory/${params.userId}/${params.historyId}`, { ...params }),
    jobBoardTag: (params: any = undefined): any =>
      this.postRequest('DELETE', `jobpointe/job_board_tags/${params.id}`, params),
    jobBoardTagAssignment: (params: any = undefined): any =>
      this.postRequest('DELETE', `jobpointe/job_employee_job_board_tags/${params.id}`, params),
  };

  list = {};

  transferList = {};

  add(item) {
    const currentList = this.selectedItemsSource.value;
    let updatedList = [...currentList, item];
    updatedList = this.filterDuplicates(updatedList);
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  remove(id: number) {
    const currentList = this.selectedItemsSource.value;
    const updatedList = currentList.filter(row => {
      return row.id !== id;
    });
    this.selectedItemsSource.next(updatedList);
    this.saveToLocalStorage();
  }

  saveToLocalStorage() {
    localStorage.setItem('selected-jobs', JSON.stringify(this.selectedItemsSource.value));
  }

  filterDuplicates(list: Array<any>) {
    return list.filter((e, i) => list.findIndex(obj => obj.id == e.id) === i);
  }

  private postRequest(
    requestType: string,
    url: string,
    data: any = '',
    showLoader: boolean = false,
    hideLoader: boolean = false,
  ): Observable<any> {
    // if GET request and param data was received, format query string and reset data to be sent in body
    if (requestType === 'GET' && data) {
      const queryString = buildQueryString(data);
      url += queryString;
      data = '';
    }

    this.HttpRequestModel.reset();
    this.HttpRequestModel.method = requestType;
    this.HttpRequestModel.url = url;
    this.HttpRequestModel.body = data;

    return this.httpService.init(this.HttpRequestModel, showLoader, hideLoader).pipe(map(response => response));
  }
  private downloadRequest(
    requestType: string,
    url: string,
    data: any = '',
    showLoader: boolean = false,
    hideLoader: boolean = false,
  ): Observable<any> {
    if (data) {
      const queryString = buildQueryString(data);
      url += queryString;
      data = '';
    }
    this.HttpRequestModel.reset();
    this.HttpRequestModel.method = requestType;
    this.HttpRequestModel.url = url;
    this.HttpRequestModel.responseType = 'blob';
    this.HttpRequestModel.observe = 'response';

    return this.httpService.init(this.HttpRequestModel, showLoader, hideLoader).pipe(map(response => response));
  }
}
